import {
  incrementXP,
  checkLevelUp,
  levelUp,
  experienceUntilLevel,
  percentUntilLevel,
} from './xp'

const NO_OP = () => {}
const MONETIZATION_PROGRESS = 'monetizationprogress'
const REQUIRED_PARAMS = ['xp', 'level', 'levelThresholds']

/**
 * RankedProfile
 *
 */
const RankedProfile = () => {
  let currentXP
  let currentLevel
  let thresholds
  let multiplier
  let cbs = {}
  let active = false

  /**
   * This function sets up the initial properties, any callback functions,
   * and sets the event listener for the monetizationprogress event
   *
   * @param {Object} params the params for initializing
   * @param {number} params.xp the user's current experience
   * @param {number} params.level the users current level
   * @param {Array} params.levelThresholds the experience thresholds between levels
   * @param {number} [params.multiplier] a multiplier to use when incrementing experience
   * @param {Function} [params.onXPUpdate] optional callback for when experience updates
   * @param {Function} [params.onLevelUp] optional callback for when user levels up
   * @returns {void}
   */
  function init(params) {
    if (!document.monetization) {
      console.info('Monetization not initialized')
      return
    }

    // Make sure the required parameters were passed
    for (const key of REQUIRED_PARAMS) {
      if (params[key] === undefined || params[key] === null) {
        console.error(`${key} must be provided when initializing!`)
        return
      }
    }

    // Initialize variables
    currentXP = params.xp || 0
    currentLevel = params.level || 1
    thresholds = params.levelThresholds
    multiplier = params.multiplier || 1
    cbs.onXPUpdate = params.onXPUpdate || NO_OP
    cbs.onLevelUp = params.onLevelUp || NO_OP
    active = true

    startMonetization()
  }

  /**
   * This function is called on every iteration of the monetizationprogress
   * event. It takes in an event object containing the amount received, and
   * the scale (number of places past the decimal for the amount)
   * Read more: https://webmonetization.org/docs/api
   *
   * @param {Object} detail the detail param pulled from the event
   * @param {number} detail.assetScale the scale to use with the amount
   * @param {number} detail.amount the destination amount received
   * @returns {void}
   */
  function updateXP({ detail }) {
    try {
      const { amount, assetScale } = detail

      currentXP = incrementXP({ assetScale, amount, currentXP, multiplier })
      cbs.onXPUpdate && cbs.onXPUpdate(currentXP)

      if (checkLevelUp(currentXP, thresholds[currentLevel - 1])) {
        currentLevel = levelUp(currentLevel)
        cbs.onLevelUp && cbs.onLevelUp(currentLevel)
      }
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * This function calls a helper function that returns the amount of
   * experience needed until the next level
   *
   * @returns {number} the amount of experience left
   */
  function getExperienceToNextLevel() {
    return experienceUntilLevel({
      currentLevel,
      currentXP,
      levelThresholds: thresholds,
    })
  }

  /**
   * This function calls a helper function that returns the percentage
   * between the user's current level and the next level
   *
   * @returns {number} the amount of experience left
   */
  function getPercentageTowardsLevel() {
    return percentUntilLevel({
      currentLevel,
      currentXP,
      levelThresholds: thresholds,
    })
  }

  /**
   * This function returns the user's current experience
   *
   * @returns {number} the user's experience
   */
  function getCurrentXP() {
    return currentXP
  }

  /**
   * This function returns the user's current level
   *
   * @returns {number} the user's level
   */
  function getCurrentLevel() {
    return currentLevel
  }

  /**
   * This function adds the monetization progress event listener
   *
   */
  function startMonetization() {
    active = true

    document.monetization.addEventListener(MONETIZATION_PROGRESS, updateXP)
  }

  /**
   * This function removes the monetization progress event listener
   *
   */
  function stopMonetization() {
    active = false

    document.monetization.removeEventListener(
      MONETIZATION_PROGRESS,
      incrementXP
    )
  }

  /**
   * This function returns whether monetization is active
   *
   */
  function isActive() {
    return active
  }

  /**
   * This function should be called for cleaning up references and
   * event listeners when no longer needed
   *
   * @returns {void}
   */
  function cleanup() {
    active = false
    cbs = null

    stopMonetization()
  }

  return {
    init,
    getCurrentXP,
    getExperienceToNextLevel,
    getPercentageTowardsLevel,
    getCurrentLevel,
    startMonetization,
    stopMonetization,
    isActive,
    cleanup,
  }
}

export default RankedProfile
